<template>
    <Modal :show="visible" width="large" @update:show="close">
        <div class="flex items-center gap-2 mb-4">
            <h3 class="text-base font-semibold text-ui-active">GitHub Sync</h3>
            <span v-if="props.designName" class="font-mono text-xs text-gray-400">
                {{ props.designName }}
            </span>
            <span v-if="!isOnline"
                class="ml-2 text-xs px-2 py-0.5 rounded bg-orange-900/40 text-orange-300 border border-orange-500/30">
                Offline
            </span>
            <div v-else-if="!isConfigured" class="ml-auto text-xs text-yellow-400">
                Configure GitHub in Settings first
            </div>
        </div>

        <!-- Tab bar -->
        <div class="flex gap-1 mb-4 border-b border-white/20">
            <button v-for="tab in tabs" :key="tab.id"
                class="px-3 py-1.5 text-xs rounded-t transition-colors"
                :class="activeTab === tab.id
                    ? 'bg-white/15 text-white border-b-2 border-ui-active -mb-px'
                    : 'text-gray-400 hover:text-white'"
                @click="activeTab = tab.id">
                {{ tab.label }}
            </button>
        </div>

        <!-- ── PUSH ──────────────────────────────────────────────── -->
        <div v-if="activeTab === 'push'" class="space-y-3 w-full">
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-1/3 shrink-0 text-xs text-gray-400">Package</span>
                <span class="flex-1 font-mono text-xs text-ui-active">
                    {{ props.designName || '(no design loaded)' }}
                </span>
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-1/3 shrink-0 text-xs text-gray-400">Destination</span>
                <span v-if="isOnline" class="flex-1 font-mono text-xs text-gray-400 truncate">
                    {{ g.owner }}/{{ g.repo }}/{{ destinationHint }}
                </span>
                <span v-else class="flex-1 text-xs text-orange-300/70">
                    Local cache — syncs to GitHub when back online
                </span>
            </div>
            <div v-if="isOnline">
                <label class="block text-xs font-medium text-gray-400 mb-1">Commit message</label>
                <input v-model="commitMessage"
                    class="w-full p-2 border border-white/20 rounded bg-white/15 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-ui-active"
                    placeholder="Describe what changed…" />
            </div>
            <div v-else class="text-xs text-gray-500 px-0.5">
                Offline — the package will be saved locally and you can push to GitHub when back online.
            </div>

            <StatusBanner :status="pushStatus" :message="pushMessage" />

            <div class="flex justify-end gap-2 pt-1">
                <form-button color="gray" class="w-24" @click="close">Cancel</form-button>
                <form-button
                    :color="isOnline ? 'success' : 'theme'"
                    :disabled="!props.designName || pushing || (isOnline && !isConfigured)"
                    @click="doPush">
                    <span v-if="pushing">{{ isOnline ? 'Pushing…' : 'Saving…' }}</span>
                    <span v-else>{{ isOnline ? 'Push to GitHub' : 'Save Local' }}</span>
                </form-button>
            </div>
        </div>

        <!-- ── HISTORY ────────────────────────────────────────────── -->
        <div v-if="activeTab === 'history'" class="space-y-2 w-full">
            <div class="flex items-center gap-2 mb-1">
                <span v-if="!props.designId" class="text-xs text-gray-500">
                    No design is open.
                </span>
                <template v-else>
                    <span class="text-xs text-gray-400">Commit history for
                        <span class="font-mono text-white">{{ props.designName }}</span>
                    </span>
                    <button class="ml-auto text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded border border-white/20"
                        :disabled="historyLoading || (isOnline && !isConfigured)" @click="loadHistory">
                        {{ historyLoading ? 'Loading…' : 'Refresh' }}
                    </button>
                </template>
            </div>

            <StatusBanner :status="historyError ? 'error' : null" :message="historyError" />

            <div v-if="historyLoading" class="text-xs text-gray-500 text-center py-4">Loading…</div>

            <div v-if="!isOnline && !historyLoading" class="text-xs text-gray-500 text-center py-4">
                Offline — commit history unavailable.
            </div>

            <div v-if="isOnline && !historyLoading && historyReady && commits.length === 0"
                class="text-xs text-gray-500 text-center py-4">
                No commits found for this package yet — push it first.
            </div>

            <div v-if="isOnline" class="border border-white/20 rounded overflow-hidden">
                <div v-for="(commit, ci) in commits" :key="commit.sha"
                    class="flex items-start gap-2 px-3 py-1.5 border-b border-white/10 last:border-0 hover:bg-white/5">
                    <div class="flex flex-col items-center pt-0.5 shrink-0" style="width:14px">
                        <div class="w-2 h-2 rounded-full shrink-0" :class="ci === 0 ? 'bg-ui-active' : 'bg-white/30'"></div>
                        <div v-if="ci < commits.length - 1" class="w-px flex-1 bg-white/15 mt-0.5" style="min-height:12px"></div>
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="text-xs text-white truncate">{{ commit.commit.message }}</div>
                        <div class="text-xs text-gray-500">
                            {{ formatDate(commit.commit.author.date) }}
                            <span class="mx-1">·</span>
                            {{ commit.commit.author.name }}
                            <span class="mx-1">·</span>
                            <span class="font-mono">{{ commit.sha.slice(0, 7) }}</span>
                        </div>
                    </div>
                    <div class="shrink-0 flex items-center gap-1">
                        <span v-if="ci === 0" class="text-xs text-gray-500 pr-1">current</span>
                        <form-button v-else color="theme" :disabled="restoring !== null" @click="doRestoreVersion(commit.sha)">
                            <span v-if="restoring === commit.sha">Restoring…</span>
                            <span v-else>Restore</span>
                        </form-button>
                    </div>
                </div>
            </div>

            <StatusBanner :status="restoreStatus" :message="restoreMessage" />
        </div>

        <!-- ── LOCAL CACHE ───────────────────────────────────────────── -->
        <div v-if="activeTab === 'local'" class="space-y-2 w-full">
            <div class="flex items-center gap-2">
                <span class="text-xs text-gray-400">Local cache
                    <span class="text-gray-500">({{ localPackages.length }})</span>
                </span>
                <button class="ml-auto text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded border border-white/20"
                    :disabled="localLoading" @click="loadLocalList">
                    {{ localLoading ? 'Loading…' : 'Refresh' }}
                </button>
            </div>

            <StatusBanner :status="localError ? 'error' : null" :message="localError" />
            <StatusBanner :status="localStatus" :message="localMessage" />

            <div v-if="localPackages.length === 0 && !localLoading"
                class="text-xs text-gray-500 text-center py-4">
                No packages in local cache.
            </div>

            <div v-if="localPackages.length > 0" class="border border-white/20 rounded overflow-hidden">
                <div v-for="pkg in localPackages" :key="pkg.id"
                    class="flex items-center gap-3 px-3 py-1.5 border-b border-white/10 last:border-0">
                    <div class="flex-1 min-w-0">
                        <div class="font-mono text-xs text-white truncate">{{ pkg.name || pkg.id }}</div>
                        <div class="text-xs text-gray-500">{{ pkg.packagePath }}</div>
                    </div>
                    <span v-if="pkg._localModified"
                        class="text-xs px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-500/30 shrink-0">
                        modified
                    </span>
                    <div class="flex gap-1 shrink-0">
                        <button title="Open" class="w-6 h-6 rounded text-xs flex items-center justify-center bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white"
                            :disabled="openingId === pkg.id" @click="doOpenLocal(pkg)">
                            {{ openingId === pkg.id ? '…' : '↗' }}
                        </button>
                        <button title="Delete from local cache"
                            class="w-6 h-6 rounded text-xs flex items-center justify-center bg-red-700/50 text-red-300 hover:bg-red-600/60"
                            :disabled="deletingId === pkg.id" @click="doDeleteLocal(pkg)">
                            {{ deletingId === pkg.id ? '…' : '✕' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── BROWSE ─────────────────────────────────────────────── -->
        <div v-if="activeTab === 'browse'" class="flex flex-col gap-2 w-full">
            <div class="flex items-center gap-2 w-full">
                <span class="text-xs text-gray-400">
                    {{ isOnline ? 'All packages in' : 'Local cache —' }}
                    <span v-if="isOnline" class="font-mono">{{ g.folder || 'designs' }}</span>
                </span>
                <button class="ml-auto text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded border border-white/20"
                    :disabled="browseLoading || (isOnline && !isConfigured)" @click="loadBrowseList">
                    {{ browseLoading ? 'Loading…' : 'Refresh' }}
                </button>
            </div>

            <StatusBanner :status="browseError ? 'error' : null" :message="browseError" />

            <div v-if="!browseLoading && browseReady && browsePackages.length === 0"
                class="text-xs text-gray-500 text-center py-3">
                No design packages found.
            </div>

            <div v-if="browsePackages.length > 0" class="border border-white/20 rounded overflow-hidden">
                <div v-for="pkg in browsePackages" :key="pkg.id"
                    class="flex items-center gap-2 px-3 py-2 border-b border-white/10 last:border-0 hover:bg-white/10">
                    <div class="flex-1 min-w-0">
                        <div class="font-mono text-xs text-white truncate">{{ pkg.name || pkg.id }}</div>
                        <div v-if="pkg.description" class="text-xs text-gray-500 truncate">{{ pkg.description }}</div>
                    </div>
                    <span v-if="pkg._localModified" class="text-xs text-yellow-400 shrink-0" title="Local changes pending push">↑</span>
                    <form-button color="success" :disabled="openingId === pkg.id" @click="doOpenFromBrowse(pkg)">
                        <span v-if="openingId === pkg.id">Loading…</span>
                        <span v-else>Open</span>
                    </form-button>
                    <button v-if="isOnline" title="Delete from GitHub"
                        class="text-xs px-1.5 py-0.5 rounded bg-white/5 text-gray-500 hover:bg-red-900/40 hover:text-red-300 shrink-0 transition-colors"
                        @click="doDeleteRemote(pkg)">
                        Delete
                    </button>
                </div>
            </div>

            <StatusBanner :status="restoreStatus" :message="restoreMessage" />
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, h } from 'vue';
import Modal from '../Modal.vue';
import {
    listPackages, pushPackage, getPackageAtRef, getPackageHistory,
    deleteRemotePackage, listLocalPackages, deleteLocalPackage, loadCachedPackage,
} from '../../github/sync.js';
import { slugify } from '../../package/slug.js';
import { base64ToText } from '../../package/base64.js';

// ─── StatusBanner inline component (render fn — no runtime compiler needed) ──
const StatusBanner = {
    props: { status: String, message: String },
    setup(props) {
        return () => {
            if (!props.message) return null
            const cls = `text-xs px-2 py-1.5 rounded ${
                props.status === 'success' ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300'
            }`
            return h('div', { class: cls }, props.message)
        }
    },
};

// ─── Props / emits ────────────────────────────────────────────────────────────
const props = defineProps({
    visible:      { type: Boolean,  default: false },
    settings:     { type: Object,   required: true },
    designId:     { type: String,   default: '' },
    designName:   { type: String,   default: '' },
    // async ({name, description, author}) => DesignPackage
    buildPackage: { type: Function, default: null },
});

const emit = defineEmits(['update:visible', 'load-package', 'package-saved']);

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const tabs = [
    { id: 'push',    label: 'Push' },
    { id: 'local',   label: 'Local' },
    { id: 'history', label: 'History' },
    { id: 'browse',  label: 'Browse' },
];
const activeTab = ref('push');

// ─── Convenience ─────────────────────────────────────────────────────────────
const g = computed(() => props.settings?.github ?? {});
const isConfigured = computed(() => !!(g.value.token && g.value.owner && g.value.repo && g.value.branch));
const destinationHint = computed(() => `${g.value.folder || 'designs'}/${slugify(props.designName || props.designId || 'design')}`);

// ─── Online / offline state ───────────────────────────────────────────────────
const isOnline = ref(navigator.onLine);
function handleOnline()  { isOnline.value = true; }
function handleOffline() { isOnline.value = false; }
onMounted(() => {
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
});
onUnmounted(() => {
    window.removeEventListener('online',  handleOnline);
    window.removeEventListener('offline', handleOffline);
});

// ─── Push ─────────────────────────────────────────────────────────────────────
const commitMessage = ref('');
const pushing       = ref(false);
const pushStatus    = ref(null);
const pushMessage   = ref('');

async function doPush() {
    pushStatus.value  = null;
    pushMessage.value = '';
    pushing.value     = true;
    try {
        if (!props.buildPackage) throw new Error('Nothing to save yet');
        const pkg = await props.buildPackage({ name: props.designName });
        const result = await pushPackage(g.value, pkg, commitMessage.value || `Update ${props.designName}`);

        pushStatus.value  = 'success';
        pushMessage.value = result._local
            ? 'Saved locally — push to GitHub when back online'
            : `Pushed to ${g.value.owner}/${g.value.repo}/${result.path}`;
        emit('package-saved', pkg);
        historyReady.value = false;
        browseReady.value  = false;
    } catch (e) {
        pushStatus.value  = 'error';
        pushMessage.value = e.message;
    } finally {
        pushing.value = false;
    }
}

// ─── History tab ──────────────────────────────────────────────────────────────
const commits        = ref([]);
const historyLoading  = ref(false);
const historyReady    = ref(false);
const historyError    = ref('');

async function loadHistory() {
    if (!props.designId) return;
    if (isOnline.value && !isConfigured.value) return;
    historyLoading.value = true;
    historyError.value   = '';
    try {
        commits.value = isOnline.value ? await getPackageHistory(g.value, props.designId) : [];
        historyReady.value = true;
    } catch (e) {
        historyError.value = e.message;
        commits.value = [];
    } finally {
        historyLoading.value = false;
    }
}

// ─── Restore (History tab) — reconstructs + imports + pushes back as a new commit ──
const restoring      = ref(null);
const restoreStatus  = ref(null);
const restoreMessage = ref('');

async function doRestoreVersion(sha) {
    restoring.value      = sha;
    restoreStatus.value  = null;
    restoreMessage.value = '';
    try {
        const pkg = await getPackageAtRef(g.value, props.designId, sha);
        emit('load-package', pkg);
        // Restoring a version creates a new commit — it doesn't just load locally.
        await pushPackage(g.value, pkg, `Restore ${props.designName} to ${sha.slice(0, 7)}`);
        historyReady.value = false;
        close();
    } catch (e) {
        restoreStatus.value  = 'error';
        restoreMessage.value = e.message;
    } finally {
        restoring.value = null;
    }
}

// ─── Browse tab — opens ANY package (no restore-commit; this isn't "restore a version of the current design") ──
const browsePackages = ref([]);
const browseLoading  = ref(false);
const browseReady    = ref(false);
const browseError    = ref('');
const openingId       = ref(null);

async function loadBrowseList() {
    if (isOnline.value && !isConfigured.value) return;
    browseLoading.value = true;
    browseError.value   = '';
    browseReady.value   = false;
    try {
        browsePackages.value = await listPackages(g.value);
        browseReady.value = true;
    } catch (e) {
        browseError.value = e.message;
    } finally {
        browseLoading.value = false;
    }
}

async function doOpenFromBrowse(pkg) {
    openingId.value = pkg.id;
    restoreStatus.value = null;
    restoreMessage.value = '';
    try {
        const history = await getPackageHistory(g.value, pkg.id).catch(() => []);
        const headSha = history?.[0]?.sha;
        if (!headSha) throw new Error('No commits found for this package');
        const full = await getPackageAtRef(g.value, pkg.id, headSha);
        emit('load-package', full);
        close();
    } catch (e) {
        restoreStatus.value = 'error';
        restoreMessage.value = e.message;
    } finally {
        openingId.value = null;
    }
}

async function doDeleteRemote(pkg) {
    restoreStatus.value = null;
    restoreMessage.value = '';
    try {
        await deleteRemotePackage(g.value, pkg.id);
        browsePackages.value = browsePackages.value.filter(p => p.id !== pkg.id);
        loadLocalList();
    } catch (e) {
        restoreStatus.value = 'error';
        restoreMessage.value = e.message;
    }
}

// ─── Local cache tab ──────────────────────────────────────────────────────────
const localPackages = ref([]);
const localLoading  = ref(false);
const localError    = ref('');
const localStatus   = ref(null);
const localMessage  = ref('');
const deletingId     = ref(null);

async function loadLocalList() {
    localLoading.value = true;
    localError.value   = '';
    try {
        localPackages.value = (await listLocalPackages()).map(row => ({
            id: row.packageId,
            packagePath: row.packagePath,
            name: (() => { try { return JSON.parse(row.encoding === 'base64' ? base64ToText(row.content) : row.content).name; } catch { return null; } })(),
            _localModified: row.localModified,
        }));
    } catch (e) {
        localError.value = e.message;
    } finally {
        localLoading.value = false;
    }
}

async function doOpenLocal(pkg) {
    openingId.value = pkg.id;
    try {
        const full = await loadCachedPackage(pkg.id);
        if (!full) throw new Error('Package not found in local cache');
        emit('load-package', full);
        close();
    } catch (e) {
        localError.value = e.message;
    } finally {
        openingId.value = null;
    }
}

async function doDeleteLocal(pkg) {
    deletingId.value = pkg.id;
    localError.value = '';
    try {
        await deleteLocalPackage(pkg.id);
        localPackages.value = localPackages.value.filter(p => p.id !== pkg.id);
        browseReady.value = false;
    } catch (e) {
        localError.value = e.message;
    } finally {
        deletingId.value = null;
    }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
watch(() => props.visible, (val) => {
    if (!val) return;
    pushStatus.value     = null;
    pushMessage.value    = '';
    restoreStatus.value  = null;
    restoreMessage.value = '';
    commitMessage.value  = props.designName ? `Update ${props.designName}` : '';

    const canLoad = isConfigured.value || !isOnline.value;
    if (activeTab.value === 'history' && canLoad && !historyReady.value) loadHistory();
});

watch(activeTab, (tab) => {
    const canLoad = isConfigured.value || !isOnline.value;
    if (tab === 'local'   && !localLoading.value)                                     loadLocalList();
    if (tab === 'history' && canLoad && !historyReady.value && !historyLoading.value) loadHistory();
    if (tab === 'browse'  && canLoad && !browseReady.value  && !browseLoading.value)  loadBrowseList();
});

function close() { emit('update:visible', false); }

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
}
</script>
